/**
 * Mouse drag-to-scroll for `.section-mobile-carousel` containers (the CSS
 * scroll-snap utility in base.css, used by editorial-grid and instagram-grid).
 *
 * Touch devices scroll these natively; a mouse cannot drag an overflow
 * container, which leaves the theme editor's mobile preview (and any
 * small-viewport desktop window) stuck on the first card. This adds
 * click-drag scrolling for mouse pointers only.
 *
 * Delegated listeners on the document, so it needs no per-section init and
 * keeps working when the theme editor re-renders a section.
 */

const DRAG_THRESHOLD_PX = 5;

document.addEventListener('pointerdown', (event) => {
  if (event.pointerType !== 'mouse' || event.button !== 0) return;
  if (!(event.target instanceof Element)) return;

  const carousel = event.target.closest('.section-mobile-carousel');
  if (!(carousel instanceof HTMLElement)) return;
  if (carousel.scrollWidth <= carousel.clientWidth) return;

  const startX = event.clientX;
  const startScrollLeft = carousel.scrollLeft;
  let dragging = false;

  /** @param {PointerEvent} moveEvent */
  const onMove = (moveEvent) => {
    const delta = moveEvent.clientX - startX;

    if (!dragging && Math.abs(delta) > DRAG_THRESHOLD_PX) {
      dragging = true;
      // Snap and smooth scrolling both fight a live drag — suspend them until release.
      carousel.style.scrollSnapType = 'none';
      carousel.style.scrollBehavior = 'auto';
      carousel.setPointerCapture(event.pointerId);
    }

    if (dragging) {
      moveEvent.preventDefault();
      carousel.scrollLeft = startScrollLeft - delta;
    }
  };

  const onUp = () => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', onUp);

    if (!dragging) return;

    // The click that fires on release would navigate into the card that
    // happens to be under the cursor — swallow exactly that one click.
    carousel.addEventListener(
      'click',
      (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
      },
      { capture: true, once: true }
    );

    // Restoring snap makes the browser settle on the nearest snap point.
    carousel.style.scrollSnapType = '';
    carousel.style.scrollBehavior = '';
  };

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', onUp);
});

// Card images are draggable by default and hijack the gesture — never inside a carousel.
document.addEventListener('dragstart', (event) => {
  if (event.target instanceof Element && event.target.closest('.section-mobile-carousel')) {
    event.preventDefault();
  }
});

/*
 * Start-of-row guard (2026-10-06). On iPhone (seen on Safari, iPhone 16 Pro Max) the
 * editorial carousel opened already scrolled to its LAST card. Mandatory scroll-snap
 * plus a late layout pass (images arriving, the section's reveal, fonts) lets WebKit
 * re-snap on its own, and with the last card's `scroll-snap-align: end` it can settle
 * on the end. Whatever the trigger, nobody asked for that scroll: until the visitor
 * touches, clicks, wheels or keys inside a carousel, any scroll it makes is put back
 * to the first card. No time limit: the cards' images load lazily as the section nears
 * the viewport, so the stray re-snap can come long after page load. The guard stands
 * down for good on the first interaction with that carousel.
 */
const guarded = new WeakSet();

/** @param {HTMLElement} carousel */
function guardStart(carousel) {
  if (guarded.has(carousel)) return;
  guarded.add(carousel);

  let active = true;
  const stop = () => {
    active = false;
    carousel.removeEventListener('scroll', onScroll);
  };
  const reset = () => {
    if (!active || carousel.scrollLeft === 0) return;
    carousel.style.scrollBehavior = 'auto'; // jump, don't animate the correction
    carousel.scrollLeft = 0;
    carousel.style.scrollBehavior = '';
  };
  const onScroll = () => reset();

  for (const type of ['pointerdown', 'touchstart', 'wheel', 'keydown']) {
    carousel.addEventListener(type, stop, { passive: true, once: true });
  }
  carousel.addEventListener('scroll', onScroll, { passive: true });
  reset();
  if (document.readyState !== 'complete') window.addEventListener('load', reset, { once: true });
}

function guardAll() {
  for (const carousel of document.querySelectorAll('.section-mobile-carousel')) {
    if (carousel instanceof HTMLElement) guardStart(carousel);
  }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', guardAll);
else guardAll();
document.addEventListener('shopify:section:load', guardAll);
